import React, { useState, useMemo, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Typography from '@mui/material/Typography';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import {
  useAccount, 
  useDisconnect, 
  useSwitchChain,
  useBalance,
  useWriteContract,
  useWaitForTransactionReceipt,
  useSendTransaction,
} from 'wagmi';
import { parseUnits } from 'viem';
import { getTokenInfo, getChainId, BLOCK_EXPLORERS } from '../utils/networkTokens';
import debugLog from '../utils/debugLog';
import normalizeWalletError from '../utils/normalizeWalletError';
import { toEpochMs } from '../utils/time';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import Modal from '@mui/material/Modal';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// ERC20 ABI for token transfers
const tokenAbi = [
  {
    name: 'transfer',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'to', type: 'address' },
      { name: 'amount', type: 'uint256' }
    ],
    outputs: [{ name: '', type: 'bool' }]
  }
];

// Define the ButtonDivider component before using it
const ButtonDivider = styled(Divider)(({ theme }) => ({
  height: 24,
  margin: '0 8px',
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
}));

const WalletButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0052FF', // Base blue color
  color: 'white',
  '&:hover': {
    backgroundColor: '#0039B6',
  },
  marginTop: theme.spacing(2),
  width: '100%'
}));

// Format token amount for display and calculations
const formatTokenAmount = (amount, decimals = 18) => {
  // Handle null, undefined or NaN
  if (amount === null || amount === undefined || (typeof amount === 'number' && isNaN(amount))) {
    return '0';
  }
  
  // Convert to string if it's not already
  const strAmount = String(amount);
  
  // Check if it's in scientific notation (contains 'e' or 'E')
  if (strAmount.includes('e') || strAmount.includes('E')) {
    // Convert scientific notation to a regular decimal string
    const floatVal = parseFloat(strAmount);
    
    // Use BigNumber or similar library for precision if available
    // For now, use a technique that preserves all decimal places
    const str = floatVal.toFixed(decimals + 10); // Add extra precision
    
    // Remove trailing zeros but ensure proper decimal places for parseUnits
    const parts = str.split('.');
    if (parts.length === 2) {
      // Ensure we have at least 'decimals' number of places after decimal
      const decimalPart = parts[1].padEnd(decimals, '0');
      return `${parts[0]}.${decimalPart}`;
    }
    return str;
  }
  
  // For regular number strings, ensure proper decimal places
  if (strAmount.includes('.')) {
    const parts = strAmount.split('.');
    // Ensure we have at least 'decimals' number of places after decimal
    const decimalPart = parts[1].padEnd(decimals, '0');
    return `${parts[0]}.${decimalPart}`;
  }
  
  // For integers, add decimal point and zeros
  return `${strAmount}.${'0'.repeat(decimals)}`;
};

// Add this array for localStorage cleanup
const CLEAR_KEYS = [
  'wagmi.store',
  'wagmi.connected',
  'wagmi.injected.connected',
  'wagmi.wallet',
  'wagmi.account',
  'wagmi.chainId',
  'rainbowkit.wallet',
  '@appkit/connection_status'
];

// Network Switch Modal Component
const NetworkSwitchModal = ({ open, onClose, targetNetwork, targetChainId, currentChainId, switchChain }) => {
  const displayCurrentChainId = currentChainId || 'Unknown';
  
  // Function to attempt network switch again
  const handleSwitchNetwork = async () => {
    try {
      debugLog("Switch button clicked, attempting network switch...");
      debugLog("Target chainId:", targetChainId);
      debugLog("SwitchChain function available:", !!switchChain);
      
      // Try to request the wallet to switch networks
      if (switchChain) {
        try {
          debugLog("Attempting to switch network using wagmi...");
          // Convert chainId to number if it's a string
          const chainIdNumber = typeof targetChainId === 'string' 
            ? parseInt(targetChainId, 10) 
            : targetChainId;
            
          debugLog("Converting chainId to number:", chainIdNumber);
          
          // This is the key change - pass both chainId and connector to ensure
          // the correct wallet is used for network switching
          await switchChain({
            chainId: chainIdNumber,
            // The connector object should be passed from the parent component
            connector: window.connectedWalletConnector
          });
          
          debugLog("Network switch request sent to wallet");
          
          // ADDED: Verify the switch and close modal only when confirmed
          const checkInterval = setInterval(async () => {
            try {
              // Get current chain ID using wagmi - more reliable than window.ethereum
              const provider = await window.connectedWalletConnector?.getProvider();
              
              if (provider) {
                const chainIdHex = await provider.request({ method: 'eth_chainId' });
                const currentChainIdDec = parseInt(chainIdHex, 16);
                debugLog("Current chain after switch attempt:", currentChainIdDec);
                
                if (currentChainIdDec === chainIdNumber) {
                  debugLog("Network switch successful, closing modal");
                  clearInterval(checkInterval);
                  onClose();
                }
              }
            } catch (error) {
              console.error("Error checking chain ID:", error);
            }
          }, 1000);
          
          // Stop checking after 10 seconds to prevent infinite intervals
          setTimeout(() => {
            clearInterval(checkInterval);
          }, 10000);
        } catch (error) {
          console.error("Wagmi switch error:", error);
          
          // Try direct method if wagmi fails
          if (window.ethereum) {
            debugLog("Attempting direct provider network switch...");
            
            try {
              // For Base Sepolia
              if (targetChainId === 84532) {
                await window.ethereum.request({
                  method: 'wallet_addEthereumChain',
                  params: [{
                    chainId: '0x14a34', // 84532 in hex
                    chainName: 'Base Sepolia',
                    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
                    rpcUrls: ['https://sepolia.base.org'],
                    blockExplorerUrls: ['https://sepolia.basescan.org/']
                  }]
                });
              } 
              // For Base Mainnet
              else if (targetChainId === 8453) {
                await window.ethereum.request({
                  method: 'wallet_addEthereumChain',
                  params: [{
                    chainId: '0x2105', // 8453 in hex
                    chainName: 'Base',
                    nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
                    rpcUrls: ['https://mainnet.base.org'],
                    blockExplorerUrls: ['https://basescan.org/']
                  }]
                });
              }
              
              // Now try to switch to the network
              await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: `0x${targetChainId.toString(16)}` }]
              });
              
              debugLog("Direct provider network switch successful");
              onClose();
            } catch (providerError) {
              console.error("Direct provider network switch failed:", providerError);
            }
          }
        }
      } else {
        console.error("switchChain function not available");
      }
    } catch (error) {
      console.error("Network switch error:", error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="network-switch-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 360,
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)',
        p: 4,
        outline: 'none',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="network-switch-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            Network Switch Required
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ 
            bgcolor: 'rgba(0,0,0,0.04)', 
            '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' } 
          }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 3,
          p: 2, 
          bgcolor: 'rgba(244, 67, 54, 0.08)', 
          borderRadius: 2,
        }}>
          <Typography variant="body2">
            Your wallet is connected to the wrong network. Please switch to {targetNetwork} to continue.
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Current Network ID:
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="error.main">
            {displayCurrentChainId}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            Target Network ID:
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {targetChainId}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<SwapHorizIcon />}
          onClick={handleSwitchNetwork}
          sx={{ mt: 3, width: '100%' }}
        >
          Switch Network
        </Button>
        
        <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
          <AccountTreeIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
          If prompted by your wallet, approve the network switch.
        </Typography>
      </Box>
    </Modal>
  );
};

// Add this helper function to safely extract transaction hash
const extractTransactionHash = (txResult) => {
  if (!txResult) return null;
  
  // If it's a string, it's likely the hash directly
  if (typeof txResult === 'string') {
    return txResult;
  }
  
  // If it has a hash property, use that
  if (txResult.hash) {
    return txResult.hash;
  }
  
  // For other structures, try to find the hash
  if (txResult.transactionHash) {
    return txResult.transactionHash;
  }
  
  // Log the structure for debugging
  console.warn("Unknown transaction result structure:", txResult);
  return null;
};

const BaseWalletButton = ({ 
  tokenSymbol,
  tokenDecimals,
  tokenAddress,
  tokenType,
  amount,
  paymentAddress,
  networkEnvironment,
  onSuccess,
  onError,
  onTransactionSubmitted,
  onTransactionConfirmed,
  onTransactionFailed,
  expiresAt,
  onWalletConnect,
  paymentDisabled,
  onPaymentInitiated
}) => {
  const [sending, setSending] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [txHash, setTxHash] = useState(null);
  
  // Network params - ensure these use Base network
  const network = 'base'; // Must be 'base', not 'bsc'
  const environment = networkEnvironment === 'devnet' ? 'sepolia' : 'mainnet';
  const chainId = getChainId(network, environment);
  const tokenInfo = getTokenInfo(network, environment, tokenSymbol);
  const normalizedTokenType = tokenType ? String(tokenType).toLowerCase() : null;
  const isNativeToken = normalizedTokenType === 'native' || (!tokenAddress && tokenSymbol === 'ETH');
  const resolvedTokenAddress = tokenAddress || tokenInfo?.contractAddress;
  const resolvedTokenDecimals = Number.isFinite(Number(tokenDecimals))
    ? Number(tokenDecimals)
    : (tokenInfo?.decimals || 18);
  const explorerBaseUrl = BLOCK_EXPLORERS[network]?.[environment] || null;
  const buildExplorerUrl = (hash) => (explorerBaseUrl && hash ? `${explorerBaseUrl}${hash}` : null);
  
  // Add debugging to confirm network settings
  debugLog("Base Payment Environment:", {
    networkEnvironment,
    mappedEnvironment: environment, 
    chainId,
    tokenSymbol,
    tokenInfo
  });
  
  // References for preventing duplicate notifications
  const prevWalletInfoRef = useRef(null);
  const isDisconnecting = useRef(false);
  
  // Expiration checks
  const isExpiringSoon = useMemo(() => {
    if (!expiresAt) return false;
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    const timeRemainingMs = expirationTime - now;
    return timeRemainingMs < 60000 && timeRemainingMs > 0;
  }, [expiresAt]);
  
  const isExpired = useMemo(() => {
    if (!expiresAt) return false;
    const now = Date.now();
    const expirationTime = toEpochMs(expiresAt);
    if (!Number.isFinite(expirationTime)) return false;
    return now >= expirationTime;
  }, [expiresAt]);

  // wagmi hooks
  const { 
    address, 
    isConnected, 
    chain,
    connector
  } = useAccount();
  const { switchChain, isLoading: isSwitchingNetwork, chains } = useSwitchChain();
  const { disconnect } = useDisconnect();
  
  // Add balance check to ensure adequate funds
  const { data: userBalance } = useBalance({
    address: address,
    token: isNativeToken ? undefined : resolvedTokenAddress,
  });
  
  // Handle native ETH transfers
  const { sendTransactionAsync } = useSendTransaction();
  
  // Handle token transfers (ERC20)
  const { writeContractAsync: sendTokenAsync } = useWriteContract();
  
  // Watch for transaction completion (both native and token transfers)
  useWaitForTransactionReceipt({
    hash: txHash,
    chainId,
    onSuccess(data) {
      const confirmedHash = data.transactionHash || txHash;
      const explorerUrl = buildExplorerUrl(confirmedHash);
      debugLog('Transaction confirmed:', { hash: confirmedHash, explorerUrl });
      if (onTransactionConfirmed) {
        onTransactionConfirmed({
          hash: confirmedHash,
          explorerUrl,
          network,
          environment,
          tokenSymbol,
          amount
        });
      } else if (onSuccess && confirmedHash) {
        onSuccess(`Transaction confirmed! [${confirmedHash.slice(0, 8)}...]`);
      }
      setSending(false);
      setTxHash(null);
    },
    onError(error) {
      console.error("Transaction failed:", error);
      const normalizedMessage = normalizeWalletError(error);
      if (onTransactionFailed) {
        onTransactionFailed({
          hash: txHash,
          explorerUrl: buildExplorerUrl(txHash),
          network,
          environment,
          tokenSymbol,
          amount,
          errorMessage: normalizedMessage
        });
      } else if (onError) {
        onError(normalizedMessage);
      }
      setSending(false);
      setTxHash(null);
    }
  });
  
  // TESTNET WARNING - similar to BNBWalletButton.js
  useEffect(() => {
    if (environment === 'sepolia' && isConnected) {
      debugLog("TESTNET transaction detected");
      // Only show warning once per session
      const hasShownWarning = sessionStorage.getItem('base_sepolia_warning_shown');
      
      if (!hasShownWarning) {
        const message = 
          "This is a TESTNET transaction. Please ensure your wallet is:\n\n" +
          "1. Connected to Base Sepolia\n" +
          "2. Has sufficient testnet ETH (you can get from a faucet)\n\n";
        
        alert(message);
        sessionStorage.setItem('base_sepolia_warning_shown', 'true');
      }
    }
  }, [environment, isConnected]);
  
  // Compute button disabled state
  const isButtonDisabled = useMemo(() => {
    return !isConnected || 
      sending || 
      isExpiringSoon ||
      isExpired ||
      isSwitchingNetwork ||
      paymentDisabled;
  }, [
    isConnected, 
    sending, 
    isExpiringSoon,
    isExpired,
    isSwitchingNetwork,
    paymentDisabled
  ]);
  
  // Disconnect wallet and clear local storage
  const handleDisconnect = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    debugLog("Disconnecting wallet and clearing storage");
    isDisconnecting.current = true;
    
    // Clear all localStorage items that might keep connections
    CLEAR_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        console.error(`Error removing ${key}:`, err);
      }
    });
    
    // wagmi disconnect
    disconnect();
    
    debugLog("Wallet disconnected");
    
    // Reset flag after a short delay
    setTimeout(() => {
      isDisconnecting.current = false;
    }, 1000);
  };
  
  // Handle the payment process
  const handlePayment = async () => {
    if (isButtonDisabled) return;
    
    setSending(true);
    
    try {
      // Double check if the user is connected to the correct network
      const currentChainIdValue = chain?.id;
      
      // Log detailed network information for debugging
      debugLog("Network check before payment:", {
        currentChainId: currentChainIdValue,
        targetChainId: chainId,
        networkEnvironment,
        environment,
        isDevnet: networkEnvironment === 'devnet',
        shouldUseSepolia: environment === 'sepolia'
      });
      
      if (currentChainIdValue !== chainId) {
        debugLog("Network mismatch:", {
          currentChainId: currentChainIdValue, 
          targetChainId: chainId,
          targetNetwork: environment === 'sepolia' ? 'Base Sepolia' : 'Base Mainnet'
        });
        
        setShowNetworkModal(true);
        setSending(false);
        return;
      }
      
      debugLog("Network is correct:", {currentChainId: currentChainIdValue, targetChainId: chainId});
      if (onPaymentInitiated) {
        onPaymentInitiated();
      }
      
      // Parse amounts here
      let parsedAmount;
      let parsedTokenAmount;

      try {
        // For native ETH, parse amount to wei (18 decimals)
        if (isNativeToken) {
          debugLog("Preparing native ETH transaction:", {
            amount: amount,
            formatted: formatTokenAmount(amount, resolvedTokenDecimals),
            currentChainId: currentChainIdValue,
            targetChainId: chainId
          });
          
          parsedAmount = parseUnits(
            formatTokenAmount(amount, resolvedTokenDecimals), 
            resolvedTokenDecimals
          );
        } 
        // For tokens, use token's decimals
        else if (resolvedTokenAddress) {
          parsedTokenAmount = parseUnits(
            formatTokenAmount(amount, resolvedTokenDecimals), 
            resolvedTokenDecimals
          );
        }
      } catch (parseError) {
        console.error("Error parsing amount:", parseError);
        onError(normalizeWalletError(parseError, 'Amount parsing error. Please refresh and try again.'));
        setSending(false);
        return;
      }
      
      // Send the transaction
      try {
        // If native ETH transfer
        if (isNativeToken) {
          debugLog("Sending native ETH transaction:", {
            to: paymentAddress,
            value: parsedAmount
          });
          
          const txResult = await sendTransactionAsync({
            to: paymentAddress,
            value: parsedAmount
          });
          
          // Extract the transaction hash based on the result structure
          const transactionHash = extractTransactionHash(txResult);
          
          if (!transactionHash) {
            throw new Error("No transaction hash returned");
          }
          
          setTxHash(transactionHash);
          debugLog("Native ETH transaction hash:", transactionHash);
          
          // Get explorer URL from networkTokens
          const explorerUrl = buildExplorerUrl(transactionHash);
          
          if (onTransactionSubmitted) {
            onTransactionSubmitted({
              hash: transactionHash,
              explorerUrl,
              network,
              environment,
              tokenSymbol,
              amount
            });
          } else if (onSuccess) {
            onSuccess(`Transaction submitted! [${transactionHash.slice(0, 8)}...]`);
          }
        }
        // If token transfer
        else if (resolvedTokenAddress && sendTokenAsync) {
          // Debug the token transfer attempt
          debugLog(`Attempting ${tokenSymbol} token transfer:`, {
            contractAddress: resolvedTokenAddress,
            decimals: resolvedTokenDecimals,
            hasTransferFunction: !!sendTokenAsync,
            parsedAmount: parsedTokenAmount.toString()
          });
          
          try {
            const txResult = await sendTokenAsync({
              address: resolvedTokenAddress,
              abi: tokenAbi,
              functionName: 'transfer',
              args: [paymentAddress, parsedTokenAmount],
            });
            
            const transactionHash = extractTransactionHash(txResult);
            debugLog(`${tokenSymbol} transaction hash:`, transactionHash);
            if (!transactionHash) {
              throw new Error("No transaction hash returned");
            }
            setTxHash(transactionHash);
            if (onTransactionSubmitted) {
              onTransactionSubmitted({
                hash: transactionHash,
                explorerUrl: buildExplorerUrl(transactionHash),
                network,
                environment,
                tokenSymbol,
                amount
              });
            } else if (onSuccess) {
              onSuccess(`Transaction submitted! [${transactionHash.slice(0, 8)}...]`);
            }
          } catch (tokenTxError) {
            console.error(`${tokenSymbol} transaction error:`, tokenTxError);
            throw tokenTxError;
          }
        }
        else {
          throw new Error(`Unable to process ${tokenSymbol} payment. Token information or transfer method unavailable.`);
        }
      } catch (txError) {
        console.error("Transaction error:", txError);
        
        // Create a safe error message even if txError is undefined or null
        const errorMessage = normalizeWalletError(txError);
          
        if (onError) {
          onError(errorMessage);
        } else {
          console.error("onError callback not available:", errorMessage);
        }
        
        setSending(false);
      }
    } catch (error) {
      console.error("Error:", error);
      onError(error.message || "An error occurred");
      setSending(false);
    }
  };
  
  // Fix the wallet connection useEffect to prevent infinite loops
  useEffect(() => {
    // Only run this effect for meaningful changes
    if (isConnected && address) {
      // Prevent excessive logging - only log when relevant values change
      const currentWalletInfo = {
        address,
        chainId: chain?.id,
        connector: connector?.name,
        balance: userBalance?.formatted,
      };
      
      // Check if wallet info changed meaningfully before logging
      const prevInfo = prevWalletInfoRef.current;
      const hasChanged = !prevInfo || 
        prevInfo.address !== currentWalletInfo.address || 
        prevInfo.chainId !== currentWalletInfo.chainId ||
        prevInfo.connector !== currentWalletInfo.connector ||
        prevInfo.balance !== currentWalletInfo.balance;
      
      if (hasChanged) {
        debugLog("Wallet connection changed:", {
          address,
          chainId: chain?.id,
          connector: connector?.name,
          balance: userBalance?.formatted,
          network: chain?.name || 'Base',
          isTestnet: environment === 'sepolia'
        });
        
        // Store the connector for network switching
        if (connector) {
          window.connectedWalletConnector = connector;
        }
        
        // Store current values for comparison
        prevWalletInfoRef.current = currentWalletInfo;
        
        // Notify parent only once for meaningful changes
        if (onWalletConnect) {
          onWalletConnect({
            address,
            chainId: chain?.id,
            network: 'base',
            connector: connector?.name
          });
        }
        
      }
    }
  }, [
    isConnected, 
    address, 
    environment, 
    userBalance?.formatted,
    chain?.id,
    chain?.name,
    connector,
    connector?.name,
    onWalletConnect
  ]);

  // Add this effect for chain detection
  useEffect(() => {
    if (chain) {
      debugLog("Chain detected:", {
        id: chain.id,
        name: chain.name,
        supported: chains.some(c => c.id === chain.id)
      });
    } else {
      debugLog("No chain detected from wallet");
    }
  }, [chain, chains]);

  return (
    <>
      {/* Network Switch Modal */}
      {showNetworkModal && (
        <NetworkSwitchModal
          open={showNetworkModal}
          onClose={() => setShowNetworkModal(false)}
          targetNetwork={network === 'base' ? 'Base' : 'Unknown Network'}
          targetChainId={chainId}
          currentChainId={chain?.id}
          switchChain={switchChain}
        />
      )}
      
      {isConnected ? (
        <WalletButton
          variant="contained"
          onClick={handlePayment}
          disabled={isButtonDisabled}
          sx={{
            bgcolor: '#0052FF', // Base blue color
            '&:hover': { bgcolor: '#0039B6' },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px'
          }}
        >
          {sending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              <span>Processing...</span>
            </Box>
          ) : (
            <>
              <Box
                sx={{ flexGrow: 1, textAlign: 'center' }}
              >
                <Tooltip 
                  title={`Confirm transaction on ${environment === 'mainnet' ? 'Base' : 'Base Sepolia'}`}
                  placement="top"
                  PopperProps={{
                    modifiers: [{
                      name: 'preventOverflow',
                      options: {
                        altBoundary: true,
                      },
                    }],
                  }}
                >
                  <span>{`Pay with ${tokenSymbol}`}</span>
                </Tooltip>
              </Box>
              <ButtonDivider orientation="vertical" flexItem />
              {/* Wrap in a Box to isolate from parent tooltip */}
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseOver={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
                onPointerOver={(e) => e.stopPropagation()}
                onPointerEnter={(e) => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Tooltip 
                  title="Disconnect wallet" 
                  placement="top"
                  PopperProps={{
                    modifiers: [{
                      name: 'preventOverflow',
                      options: {
                        altBoundary: true,
                      },
                    }],
                  }}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDisconnect(e);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDisconnect(e);
                      }
                    }}
                    sx={{ 
                      color: '#33FF33', // Bright Yoda green
                      padding: '4px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { 
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#F44336',
                      }
                    }}
                    aria-label="disconnect wallet"
                  >
                    <PowerSettingsNewIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </Box>
            </>
          )}
        </WalletButton>
      ) : (
        <ConnectButton.Custom>
          {({
            account,
            chain,
            openAccountModal,
            openChainModal,
            openConnectModal,
            mounted,
          }) => {
            return (
              <WalletButton
                onClick={openConnectModal}
                sx={{
                  bgcolor: '#0052FF', // Base blue color
                  '&:hover': { bgcolor: '#0039B6' },
                }}
                startIcon={<AccountBalanceWalletIcon />}
              >
                Connect Wallet
              </WalletButton>
            );
          }}
        </ConnectButton.Custom>
      )}
    </>
  );
};

export default BaseWalletButton;
